import { API_BASE_URL } from '../config/api.js';

// LocalStorage helpers
const getLocalStorageWishlist = (userId) => {
  try {
    const wishlist = localStorage.getItem(`wishlist_${userId}`);
    return wishlist ? JSON.parse(wishlist) : [];
  } catch (error) {
    console.error('Error reading localStorage wishlist:', error);
    return [];
  }
};

const setLocalStorageWishlist = (userId, items) => {
  try {
    localStorage.setItem(`wishlist_${userId}`, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving localStorage wishlist:', error);
  }
};

// Get Backend JWT auth token
const getAuthToken = async () => {
  try {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    // Get backend JWT token by calling google-login endpoint
    const loginResponse = await fetch(`${API_BASE_URL}/auth/google-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: user.email,
        googleId: user.uid
      })
    });

    if (!loginResponse.ok) {
      return null;
    }

    const loginData = await loginResponse.json();
    return loginData.token;
  } catch (error) {
    console.error('Error getting backend auth token:', error);
    return null;
  }
};

// Database sync queue
const getSyncQueue = () => {
  try {
    const queue = localStorage.getItem('wishlist_sync_queue');
    return queue ? JSON.parse(queue) : [];
  } catch (error) {
    return [];
  }
};

const addToSyncQueue = (operation) => {
  try {
    const queue = getSyncQueue();
    queue.push({
      ...operation,
      timestamp: Date.now(),
      id: Math.random().toString(36).substr(2, 9)
    });
    localStorage.setItem('wishlist_sync_queue', JSON.stringify(queue));
  } catch (error) {
    console.error('Error adding to sync queue:', error);
  }
};

const removeFromSyncQueue = (operationId) => {
  try {
    const queue = getSyncQueue();
    const filteredQueue = queue.filter(op => op.id !== operationId);
    localStorage.setItem('wishlist_sync_queue', JSON.stringify(filteredQueue));
  } catch (error) {
    console.error('Error removing from sync queue:', error);
  }
};

const clearSyncQueue = () => {
  try {
    localStorage.removeItem('wishlist_sync_queue');
  } catch (error) {
    console.error('Error clearing sync queue:', error);
  }
};

// Background sync processor
let isSyncing = false;
export const processSyncQueue = async () => {
  // DISABLED: Stop repeated wishlist calls to prevent 400 errors
  return;
  
  if (isSyncing) {
    return;
  }
  
  const queue = getSyncQueue();
  if (queue.length === 0) return;
  
  // Get auth token - if no token, skip syncing
  const token = await getAuthToken();
  if (!token) {
    return;
  }
  
  isSyncing = true;
  
  for (const operation of queue) {
    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/${operation.action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: operation.userId,
          productId: operation.productId
        })
      });
      
      if (response.ok) {

        // console.log(`Synced ${operation.action} for product ${operation.productId}`);
        removeFromSyncQueue(operation.id);
      } else {
        // console.warn(`Failed to sync ${operation.action} for product ${operation.productId}`);
        // If unauthorized, clear the queue as user needs to re-authenticate
        if (response.status === 401) {
          // console.log('Auth failed, clearing sync queue');

          clearSyncQueue();
          break;
        }
      }
    } catch (error) {

    //  console.warn(`Sync error for ${operation.action}:`, error.message);

    }
    
    // Small delay between operations
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  isSyncing = false;
};

// Auto-sync every 30 seconds
setInterval(processSyncQueue, 30000);

// Trigger sync when page becomes visible
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    processSyncQueue();
  }
});

export const wishlistService = {
  // Get user's wishlist - try database first, fallback to localStorage
  async getWishlist(userId) {
    try {
      if (!userId) return [];
      
      // Try database first
      try {
        const token = await getAuthToken();
        const headers = {
          'Content-Type': 'application/json'
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Disabled backend wishlist fetch to prevent repeated API calls
        const response = {
          ok: false,
          status: 404,
          json: async () => []
        };
        
        if (response.ok) {
          const data = await response.json();
          // Filter out invalid items
          const validData = data.filter(item => item.productId && item.productId !== 'undefined' && item.productId !== '');
          // Update localStorage with fresh data
          setLocalStorageWishlist(userId, validData);
          return validData;
        } else if (response.status === 404 || response.status === 401) {
          // No wishlist in database or unauthorized, use localStorage
          const localWishlist = getLocalStorageWishlist(userId);

          // Filter localStorage items too
          const validLocalWishlist = localWishlist.filter(item => item.productId && item.productId !== 'undefined' && item.productId !== '');
          return validLocalWishlist;
        } else {
          throw new Error('Backend failed');
        }
      } catch (backendError) {
        // Fallback to localStorage
        const localWishlist = getLocalStorageWishlist(userId);
        const validLocalWishlist = localWishlist.filter(item => item.productId && item.productId !== 'undefined' && item.productId !== '');
        return validLocalWishlist;
      }
    } catch (error) {
      const localWishlist = getLocalStorageWishlist(userId);
      const validLocalWishlist = localWishlist.filter(item => item.productId && item.productId !== 'undefined' && item.productId !== '');
      return validLocalWishlist;
    }
  },

  // Add product to wishlist - immediate localStorage, queued database sync
  async addToWishlist(userId, productId) {
    try {
      if (!userId || !productId) {
        throw new Error('UserId and ProductId are required');
      }

      // Immediate localStorage update for instant UI response
      const localWishlist = getLocalStorageWishlist(userId);
      if (!localWishlist.some(item => item.productId === productId)) {
        localWishlist.push({
          id: Date.now().toString(),
          productId: productId,
          userId: userId,
          createdAt: new Date().toISOString()
        });
        setLocalStorageWishlist(userId, localWishlist);
      }

      // Queue for background database sync
      addToSyncQueue({
        action: 'add',
        userId: userId,
        productId: productId
      });

      // Trigger background sync (non-blocking)
      setTimeout(() => processSyncQueue(), 1000);

      return { message: 'Added to wishlist', success: true };
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    }
  },

  // Remove product from wishlist - immediate localStorage, queued database sync
  async removeFromWishlist(userId, productId) {
    try {
      if (!userId || !productId) {
        throw new Error('UserId and ProductId are required');
      }

      // Immediate localStorage update for instant UI response
      const localWishlist = getLocalStorageWishlist(userId);
      const filteredWishlist = localWishlist.filter(item => item.productId !== productId);
      setLocalStorageWishlist(userId, filteredWishlist);

      // Queue for background database sync
      addToSyncQueue({
        action: 'remove',
        userId: userId,
        productId: productId
      });

      // Trigger background sync (non-blocking)
      setTimeout(() => processSyncQueue(), 1000);

      return { message: 'Removed from wishlist', success: true };
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw error;
    }
  },

  // Clear sync queue manually (useful for debugging or auth issues)
  clearWishlistSyncQueue() {
    clearSyncQueue();
  },

  // Check if product is in user's wishlist
  async isInWishlist(userId, productId) {
    try {
      if (!userId || !productId) return false;
      
      // Check localStorage first for immediate response
      const localWishlist = getLocalStorageWishlist(userId);
      const inLocalWishlist = localWishlist.some(item => item.productId === productId);
      
      // Also check database if possible (but don't wait)
      this.getWishlist(userId).catch(() => {}); // Fire and forget
      
      return inLocalWishlist;
    } catch (error) {
      console.error('Error checking wishlist status:', error);
      return false;
    }
  }
};
